import { Controller } from "stimulus";
import { percent, countdown } from "utils/live_poll";

const STRUCTURAL_ACTIONS = ["sync", "open", "awaiting", "resolved", "closed", "cancelled"];

// Drives the creator dashboard "Polls" column: patches tallies in place and reloads the
// live_polls_column Turbo Frame on structural poll events. The console always renders the chat
// panel beside this column, and the panel forwards every poll frame it receives from the room as
// a `chat-panel:poll` window event stamped with its event id, so this column rides the panel's
// single room connection rather than opening one of its own and only acts on frames for the
// event it is keyed to.
export default class LivePollDashboard extends Controller {
  static values = { liveEventId: Number, columnUrl: String };

  connect() {
    this.onPoll = (event) => this.received(event.detail);
    window.addEventListener("chat-panel:poll", this.onPoll);

    this.startCountdownTicker();
  }

  disconnect() {
    if (this.onPoll) {
      window.removeEventListener("chat-panel:poll", this.onPoll);
      this.onPoll = null;
    }
    if (this.ticker) {
      window.clearInterval(this.ticker);
      this.ticker = null;
    }
  }

  received(data) {
    if (!data || data.type !== "poll") return;
    if (data.live_event_id != null && Number(data.live_event_id) !== this.liveEventIdValue) return;

    if (data.action === "tally") {
      this.applyTally(data.poll);
    } else if (STRUCTURAL_ACTIONS.includes(data.action)) {
      this.reloadColumn();
    }
  }

  // The "column" partial that fills this frame renders it without a src (deliberately, to
  // avoid Turbo treating it as self-referencing), so after the first swap the frame itself no
  // longer knows its URL and calling reload() would re-fetch the bare frame request path
  // instead. This controller's own element survives every swap, so we stash the URL there
  // and set it directly rather than relying on the frame to still carry it.
  reloadColumn() {
    const frame = document.getElementById("live_polls_column");
    if (frame) frame.src = this.columnUrlValue;
  }

  applyTally(poll) {
    if (!poll) return;

    const panel = this.element.querySelector(`[data-poll-id="${poll.id}"]`);
    if (!panel) return;
    // Ignore a late tally once the panel is no longer the open/voting state - the structural
    // reload already rendered the final counts. The countdown only exists while open.
    if (!panel.querySelector("[data-live-poll-dashboard-target='countdown']")) return;

    const total = Number(poll.total_votes) || 0;
    const totalEl = panel.querySelector("[data-live-poll-dashboard-target='total']");
    if (totalEl) totalEl.textContent = total;

    (poll.options || []).forEach((option) => {
      const row = panel.querySelector(`[data-option-id="${option.id}"]`);
      if (!row) return;

      const count = Number(option.count) || 0;
      const pct = percent(count, total);

      const countEl = row.querySelector("[data-live-poll-dashboard-target='count']");
      const percentEl = row.querySelector("[data-live-poll-dashboard-target='percent']");
      const barEl = row.querySelector("[data-live-poll-dashboard-target='bar']");

      if (countEl) countEl.textContent = count;
      if (percentEl) percentEl.textContent = `${pct}%`;
      if (barEl) barEl.style.width = `${pct}%`;
    });
  }

  // Ticks the "closes in M:SS" indicator each second from its data-closes-at timestamp.
  startCountdownTicker() {
    this.ticker = window.setInterval(() => this.tickCountdown(), 1000);
    this.tickCountdown();
  }

  tickCountdown() {
    const el = this.element.querySelector("[data-live-poll-dashboard-target='countdown']");
    if (!el) return;

    const result = countdown(el.dataset.closesAt);
    if (result) el.textContent = result.label;
  }
}
